/**
 * UpiMandateApp
 * ─────────────
 * Entry component for UPI Autopay Mandate Registration.
 * Route: /upi-mandate
 *
 * Screen flow:
 *   -1  HouseholdSearch   → search household, pick a member
 *    0  ProviderSelection → choose PhonePe or Razorpay
 *    1  MandateForm       → mandate preview + QR; registers mandate + polls status
 *    2  MandateConfirmation → success / failure
 */
import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import { MandateProvider, useMandate } from "./MandateContext";
import HouseholdSearch     from "./HouseholdSearch";
import ProviderSelection   from "./ProviderSelection";
import MandateForm         from "./MandateForm";
import MandateConfirmation from "./MandateConfirmation";

// ─────────────────────────────────────────────────────────────────────────────
// Inner app — consumes MandateProvider context
// ─────────────────────────────────────────────────────────────────────────────
function InnerApp({ history, api_token }) {
  const [screen, setScreen] = useState(-1); // -1 | 0 | 1 | 2 | 3
  const [searchKey, setSearchKey] = useState(0); // incremented to force HouseholdSearch remount fresh
  const { state, update } = useMandate();

  function goTo(n) { setScreen(n); }

  function handleRetry() {
    update({ status: "idle", redirectType: "", redirectUrl: "", merchantUserId: "", authRequestId: "" });
    setScreen(1); // back to MandateForm to regenerate
  }

  function handleHome() {
    update({
      status: "idle",
      redirectType: "",
      redirectUrl: "",
      merchantUserId: "",
      authRequestId: "",
      selectedMember: null,
      selectedProvider: null,
      skippedProviderSelection: false,
      paymentVendorDetails: null,
    });
    setSearchKey((k) => k + 1);
    setScreen(-1);
  }

  return (
    <>
      {/* Always mounted so state (member list) survives back-navigation from ProviderSelection */}
      <div style={{ display: screen === -1 ? undefined : "none" }}>
        <HouseholdSearch
          key={searchKey}
          onNext={(nextScreen = 0) => goTo(nextScreen)}
          onBack={() => history.goBack()}
          api_token={api_token}
        />
      </div>
      {screen ===  0 && <ProviderSelection onNext={() => goTo(1)} onBack={() => goTo(-1)} api_token={api_token} />}
      {screen ===  1 && (
        <MandateForm
          onNext={() => goTo(2)}
          onBack={() => goTo(0)}
          api_token={api_token}
        />
      )}
      {screen ===  2 && <MandateConfirmation onHome={handleHome} onRetry={handleRetry} onBack={handleRetry} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Outer shell — provides context + 420px mobile centred container
// ─────────────────────────────────────────────────────────────────────────────
function UpiMandateApp({ history, location }) {
  const { api_token } = (location && location.state) || {};
  const resolvedToken = api_token || localStorage.getItem("api_token");
  return (
    <MandateProvider>
      <InnerApp history={history} api_token={resolvedToken} />
    </MandateProvider>
  );
}

export default withRouter(UpiMandateApp);
